import * as React from "react";
import styled from "styled-components";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import moment from "../../../utilities/Moment";
import NumberFormatter from "../../../utilities/NumberFormatter";
import { Style } from "../../../styles";
import GraphEmpty from "../../Graph/GraphEmpty";
import { DashboardController } from "../../../controllers";
import PageLoader from "../../Page/PageLoader";

const GraphContainer = styled.div`
  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    overflow-x: auto;
  }

  &::-webkit-scrollbar {
    display: none;
  }
`;

const GraphWrapper = styled.div`
  position: relative;
  height: 100%;

  @media screen and (max-width: ${Style.breakpoints.SMALL}) {
    height: 300px;
    width: 1400px;
  }
`;

interface MonthlySentInvoice {
  month: string;
  amount: number;
}

interface IProps {
  currency: string;
  numberFormat: string;
}

const SendInvoicesWidget = (props: IProps) => {
  const { currency, numberFormat } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [monthlySentInvoices, setMonthlySentInvoices] = React.useState<MonthlySentInvoice[]>([]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await DashboardController.getSentInvoicesMonthly();
      setMonthlySentInvoices(response);
    } catch (error) {
      console.error("Failed to fetch sent invoices monthly:", error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="panel h-full flex flex-col mb-0!">
        <div className="panel-header is-large">{t("Dashboard::Sent invoices")}</div>
        <div className="panel-content is-large min-h-0 flex-1 overflow-auto">
          <PageLoader />
        </div>
      </div>
    );
  }

  const labels = monthlySentInvoices.map((entry) => entry.month);
  const amounts = monthlySentInvoices.map((entry) => Number(entry.amount));

  const dataset = {
    type: "bar" as const,
    label: t("Dashboard::Amount excl. VAT"),
    backgroundColor: monthlySentInvoices.map(() => "#5541F6"),
    borderSkipped: "top" as const,
    data: amounts,
    fill: false,
  };

  const maxValue = Math.max(...amounts);
  const minValue = Math.min(...amounts);
  const suggestedMax = isFinite(maxValue) ? maxValue + (maxValue * 5) / 100 : 0;
  const suggestedMin = isFinite(minValue) ? minValue - (minValue * 5) / 100 : 0;

  const data = {
    labels,
    datasets: [dataset],
  };

  const hasData = amounts.some((amount) => Math.abs(amount) > 0);

  return (
    <div className="panel h-full flex flex-col mb-0!">
      <div className="panel-header is-large">
        {t("Dashboard::Sent invoices")}
      </div>

      <div className="panel-content is-large min-h-0 flex-1 overflow-auto">
        <GraphContainer className="h-full">
          <GraphWrapper>
            {!hasData && (
              <GraphEmpty
                title={t("Dashboard::No data available for current financial year.")}
                description={t("Dashboard::Come back later when you've added data.")}
              />
            )}
            <Bar
              data={data}
              options={{
                maintainAspectRatio: false,
                legend: {
                  display: false,
                },
                tooltips: {
                  mode: "point",
                  backgroundColor: "#212B36",
                  callbacks: {
                    label: (tooltipItem, data) => {
                      const label = data.datasets[tooltipItem.datasetIndex].label || "";
                      const value = NumberFormatter.formatCurrency(
                        currency,
                        numberFormat,
                        Math.abs(Number(tooltipItem.value))
                      );
                      return `${label}: ${value}`;
                    },
                  },
                },
                scales: {
                  xAxes: [
                    {
                      gridLines: {
                        color: "#F4F6F8",
                        lineWidth: 2,
                        borderDash: [8, 4],
                      },
                      ticks: {
                        fontSize: 13,
                        fontColor: "#637381",
                        padding: 10,
                        callback: function (value) {
                          return moment(value, "YYYY-MM").format("MMM");
                        },
                      },
                    },
                  ],
                  yAxes: [
                    {
                      gridLines: {
                        drawBorder: false,
                        color: "#F4F6F8",
                        lineWidth: 2,
                        drawTicks: false,
                        zeroLineColor: "#DFE4E8",
                        zeroLineWidth: 2,
                      },
                      ticks: {
                        fontSize: 13,
                        fontColor: "#637381",
                        padding: 20,
                        suggestedMin: suggestedMin,
                        suggestedMax: suggestedMax,
                        callback: (value) => {
                          return NumberFormatter.formatCurrency(currency, numberFormat, value);
                        },
                      },
                    },
                  ],
                },
              }}
            />
          </GraphWrapper>
        </GraphContainer>
      </div>
    </div>
  );
};

export default SendInvoicesWidget;
